import React, {createContext, PropsWithChildren, useMemo, useContext} from 'react';
import {JobTitle} from '../models/work-experience.model';
import {JobTitleService, JobTitleServiceImpl, JobTitleCreateParams} from '../services';
import {useAuth} from './auth.provider';

export interface JobTitleProviderInterface {
  jobTitleService: JobTitleService;
  createJobTitle: (title: string) => Promise<JobTitle>;
  getAllGlobalJobTitles: (page?: number, limit?: number) => Promise<{data: JobTitle[]; total: number}>;
}

export const JobTitleContext = createContext<JobTitleProviderInterface>(undefined as any as JobTitleProviderInterface);

export const useJobTitle = () => {
  const context = useContext(JobTitleContext);
  if (!context) {
    throw new Error('useJobTitle must be used within a JobTitleProvider');
  }
  return context;
};

export const JobTitleProvider: React.FC<PropsWithChildren<{}>> = ({children}) => {
  const jobTitleService: JobTitleService = useMemo(() => new JobTitleServiceImpl(), []);
  const {user} = useAuth();

  const createJobTitle = async (title: string): Promise<JobTitle> => {
    if (!user?.id) {
      throw new Error('Account ID not found in context');
    }
    const createParams = new JobTitleCreateParams(user.id, title);
    const response = await jobTitleService.createJobTitle(createParams);
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data!;
  };

  const getAllGlobalJobTitles = async (page: number = 1, limit: number = 10): Promise<{data: JobTitle[]; total: number}> => {
    const response = await jobTitleService.getAllGlobalJobTitles(page, limit);
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data!;
  };

  return (
    <JobTitleContext.Provider
      value={{
        jobTitleService,
        createJobTitle,
        getAllGlobalJobTitles,
      }}>
      {children}
    </JobTitleContext.Provider>
  );
};
